import { useEffect, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { heroBackgroundUrl, heroWords, logoUrl } from "@/components/equinox/data";

export default function HeroSection() {
  const navigate = useNavigate();
  const [activeWord, setActiveWord] = useState(0);
  const { scrollY } = useScroll();
  const orbY = useTransform(scrollY, [0, 700], [0, -90]);
  const cardY = useTransform(scrollY, [0, 700], [0, 70]);
  const headingY = useTransform(scrollY, [0, 700], [0, 35]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveWord((current) => (current + 1) % heroWords.length);
    }, 3000);
    return () => window.clearInterval(interval);
  }, []);

  const currentWord = heroWords[activeWord];

  return (
    <section
      id="top"
      className="relative overflow-hidden bg-[#030408] px-6 pb-32 pt-32 sm:px-8 lg:px-12 lg:pb-36 lg:pt-40"
      data-testid="hero-section"
    >
      <img
        src={heroBackgroundUrl}
        alt="Abstract luxury background"
        className="absolute inset-0 h-full w-full object-cover opacity-18 mix-blend-screen"
        data-testid="hero-background-image"
      />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(3,4,8,0.22),rgba(3,4,8,0.84))]" />
      <AnimatePresence mode="wait">
        <motion.div
          key={currentWord.word}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.05, ease: "easeInOut" }}
          className="absolute inset-0"
          style={{ background: currentWord.background }}
        />
      </AnimatePresence>
      <div className="ambient-sweep" />
      <div className="hero-grid absolute inset-0 opacity-40" />
      <motion.div style={{ y: orbY }} className="float-orb absolute right-[12%] top-28 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
      <motion.div style={{ y: useTransform(scrollY, [0, 700], [0, -60]) }} className="float-orb absolute bottom-12 left-[10%] h-36 w-36 rounded-full bg-blue-500/10 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl gap-16 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-24">
        <motion.div
          style={{ y: headingY }}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <div className="mb-12 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.26em] text-white/70 backdrop-blur-xl">
            <Sparkles className="h-4 w-4 text-blue-200" />
            App Growth • ASO • App Reputation
          </div>

          <div className="mb-10 flex items-center gap-4">
            <img
              src={logoUrl}
              alt="Equinox Marketing Agency logo"
              className="h-16 w-16 rounded-2xl object-cover ring-1 ring-white/10"
              data-testid="hero-logo"
            />
            <div>
              <p className="font-display text-xl font-semibold tracking-[0.2em] text-white">EQUINOX</p>
              <p className="text-sm uppercase tracking-[0.32em] text-white/[0.45]">App Growth Systems</p>
            </div>
          </div>

          <div className="section-divider relative isolate">
            <div className="hero-headline-halo" />
            <h1
              className="relative max-w-[13ch] font-display text-[2.8rem] font-semibold leading-[0.9] tracking-[-0.065em] text-white sm:text-5xl lg:text-[5.8rem]"
              data-testid="hero-heading"
            >
              <span className="block">We Help Apps</span>
              <span className="mt-1 flex min-h-[1.1em] items-start gap-3 whitespace-nowrap">
                <span className="text-white">Get</span>
                <span className="relative inline-block min-w-[7.5ch] sm:min-w-[8.5ch] lg:min-w-[9.5ch]">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={currentWord.word}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -18 }}
                      transition={{ duration: 0.45, ease: "easeOut" }}
                      className={`absolute left-0 top-0 word-glow ${currentWord.textClass}`}
                      data-testid="hero-dynamic-word"
                    >
                      {currentWord.word}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </span>
            </h1>
          </div>

          <p className="section-copy mt-9 max-w-2xl text-balance text-base text-white/70 sm:text-lg">
            ASO, App Reputation, Performance Marketing & Growth Systems for high-growth apps.
          </p>

          <div className="mt-8 grid max-w-2xl gap-4 sm:grid-cols-3" data-testid="hero-proof-cluster">
            {[
              { label: "Apps scaled", value: "70+" },
              { label: "Installs influenced", value: "3M+" },
              { label: "Average rating held", value: "4.6★" },
            ].map((item) => (
              <div key={item.label} className="rounded-[1.5rem] border border-white/[0.08] bg-white/[0.04] px-4 py-4 backdrop-blur-xl">
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.42]">{item.label}</p>
                <p className="mt-3 font-display text-2xl text-white">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => navigate("/contact")}
              className="glass-panel glass-button rounded-full bg-white px-7 py-4 text-sm font-medium text-black transition-transform duration-300 hover:scale-105"
              data-testid="cta-get-started"
            >
              <span className="relative z-10 inline-flex items-center gap-2">
                Get Your Growth Blueprint
                <ArrowRight className="h-4 w-4" />
              </span>
            </button>

            <button
              type="button"
              onClick={() => navigate("/case-studies")}
              className="glass-panel glass-button rounded-full border border-white/[0.12] bg-white/5 px-7 py-4 text-sm font-medium text-white/80 backdrop-blur-xl transition-all duration-300 hover:scale-[1.03] hover:border-white/20 hover:text-white"
              data-testid="cta-view-case-studies"
            >
              See Case Studies
            </button>
          </div>
        </motion.div>

        <motion.div
          style={{ y: cardY }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
          className="relative lg:pt-8"
        >
          <div className="panel-scan premium-border glass-panel glitter-card cinematic-card relative overflow-hidden rounded-[2.2rem] p-7 lg:p-8">
            <div className="absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent" />
            <div className="mb-7 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.28em] text-white/[0.45]">Live system layer</p>
                <p className="mt-2 font-display text-2xl text-white">Equinox Growth OS</p>
              </div>
              <div className="status-pulse rounded-full border border-white/15 bg-white/8 px-3 py-1 text-xs text-white/82">
                Active
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {[
                { label: "Automation uptime", value: "99.2%" },
                { label: "Growth loops", value: "12 live" },
                { label: "Attribution clarity", value: "+68%" },
                { label: "Decision speed", value: "3.1x faster" },
              ].map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
                  className="glitter-card metric-glow rounded-[1.6rem] border border-white/[0.08] bg-black/28 p-5"
                  data-testid={`hero-stat-${index + 1}`}
                >
                  <p className="text-sm text-white/[0.55]">{item.label}</p>
                  <p className="mt-4 font-display text-3xl text-white">{item.value}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-6 rounded-[1.6rem] border border-white/[0.08] bg-black/28 p-5 font-mono-display text-sm text-white/70">
              <div className="mb-4 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/40">
                <span className="h-2 w-2 rounded-full bg-white" />
                Intelligent orchestration
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span>Creative testing matrix</span>
                  <span className="text-blue-200">running</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>WhatsApp qualification flow</span>
                  <span className="text-white/78">syncing</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Revenue signal routing</span>
                  <span className="text-amber-200">optimising</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}